// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { CalendarCheck, Loader2, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  type TaskAvailabilityEntry,
  useCreateTaskAvailabilityMutation,
  useUpdateTaskAvailabilityMutation,
} from "@/api/hooks/use-tasks";
import { FilterSelect } from "@/components/shared/filter-select";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";

interface TaskAvailabilityDialogProps {
  subtype: string;
  /** The row being edited, or `null` when adding a new one. */
  entry: TaskAvailabilityEntry | null;
  open: boolean;
  onClose: () => void;
}

/** An empty rules box means "no rules", which is how most rows are stored. */
function parseRules(text: string): { rules: Record<string, unknown>[] | null } | { error: "invalid" | "notArray" } {
  const trimmed = text.trim();
  if (!trimmed) return { rules: null };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { error: "invalid" };
  }
  if (!Array.isArray(parsed)) return { error: "notArray" };
  return { rules: parsed as Record<string, unknown>[] };
}

/**
 * Add or edit one availability row.
 *
 * `rules` has no schema — the stored shapes vary by whatever evaluates them and most rows carry
 * none — so it is edited as JSON and only checked for being a valid array before saving. A
 * built-in type's row is an `action_task_settings` row and has no rules at all, so that box is
 * dropped for those: only priority, active and default apply.
 */
export function TaskAvailabilityDialog({ subtype, entry, open, onClose }: TaskAvailabilityDialogProps) {
  const { t } = useTranslation("back-office");
  const { data: context, isLoading: contextLoading } = useBackofficeContext();
  const createAvailability = useCreateTaskAvailabilityMutation(subtype);
  const updateAvailability = useUpdateTaskAvailabilityMutation(subtype);
  const isEdit = entry !== null;
  // A built-in row exists per store and is only ever edited, never added from here
  const isBuiltIn = entry?.kind === "builtIn";

  const [chainName, setChainName] = useState("");
  const [storeName, setStoreName] = useState("");
  const [rulesText, setRulesText] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [priority, setPriority] = useState(1);
  const [rulesError, setRulesError] = useState<string | null>(null);

  // Re-seed every time the dialog opens, so a second edit does not inherit the first one's draft
  useEffect(
    function seedFromEntry() {
      if (!open) return;
      setChainName(entry?.chainName ?? "");
      setStoreName(entry?.storeName ?? "");
      setRulesText(entry?.rules?.length ? JSON.stringify(entry.rules, null, 2) : "");
      setIsActive(entry?.isActive ?? true);
      setIsDefault(entry?.isDefault ?? false);
      setPriority(entry?.priority ?? 1);
      setRulesError(null);
    },
    [open, entry],
  );

  const saving = createAvailability.isPending || updateAvailability.isPending;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const parsed = isBuiltIn ? { rules: null } : parseRules(rulesText);
    if ("error" in parsed) {
      setRulesError(t(`tasks.availability.dialog.rules${parsed.error === "invalid" ? "Invalid" : "NotArray"}`));
      return;
    }
    setRulesError(null);
    try {
      if (isEdit && entry) {
        await updateAvailability.mutateAsync({
          availabilityId: entry.availabilityId,
          input: { rules: parsed.rules, isActive, isDefault, priority },
        });
        toast.success(t("tasks.availability.dialog.updated"));
      } else {
        await createAvailability.mutateAsync({
          chainName: chainName || undefined,
          storeName: storeName || undefined,
          rules: parsed.rules,
          isActive,
          isDefault,
          priority,
        });
        toast.success(t("tasks.availability.dialog.added"));
      }
      onClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent id="dialog-task-availability" className="sm:max-w-[42rem] p-0 gap-0 overflow-hidden flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="flex flex-row">
            <WizardPane icon={CalendarCheck} />
            <div className="flex min-w-0 flex-1 flex-col gap-4 p-6">
              <DialogHeader>
                <DialogTitle>
                  {isEdit ? t("tasks.availability.dialog.editTitle") : t("tasks.availability.dialog.addTitle")}
                </DialogTitle>
                <DialogDescription>
                  {isEdit
                    ? t("tasks.availability.dialog.editDescription")
                    : t("tasks.availability.dialog.addDescription")}
                </DialogDescription>
              </DialogHeader>

              {/* Scope is fixed once the row exists — the override it keys on cannot be moved */}
              <div className="space-y-1.5">
                <Label>{t("tasks.availability.dialog.scope")}</Label>
                {isEdit ? (
                  <p className="text-sm">
                    {entry?.storeName ?? entry?.chainName ?? (
                      <span className="text-muted-foreground">{t("tasks.scope.global")}</span>
                    )}
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <FilterSelect
                      label={t("tasks.editor.scopeChain")}
                      value={chainName}
                      onChange={(value) => {
                        setChainName(value);
                        setStoreName("");
                      }}
                      options={(context?.chains ?? []).map((chain) => ({ label: chain.display, value: chain.value }))}
                      searchPlaceholder={t("stores.form.searchChains")}
                      emptyText={t("stores.form.noChains")}
                      loading={contextLoading}
                    />
                    <FilterSelect
                      label={t("tasks.editor.scopeStore")}
                      value={storeName}
                      onChange={(value) => {
                        setStoreName(value);
                        setChainName("");
                      }}
                      options={(context?.stores ?? []).map((store) => ({ label: store.display, value: store.value }))}
                      searchPlaceholder={t("users.list.filter.searchStores")}
                      emptyText={t("users.list.filter.noStores")}
                      loading={contextLoading}
                    />
                  </div>
                )}
                {!isEdit && !chainName && !storeName && (
                  <p className="text-xs text-muted-foreground">{t("tasks.availability.dialog.scopeGlobalHint")}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="availability-priority">{t("tasks.availability.columns.priority")}</Label>
                <Input
                  id="availability-priority"
                  type="number"
                  min={0}
                  value={priority}
                  onChange={(event) => setPriority(Number(event.target.value))}
                  className="w-32"
                />
              </div>

              {!isBuiltIn && (
                <div className="space-y-1.5">
                  <Label htmlFor="availability-rules">{t("tasks.availability.columns.rules")}</Label>
                  <Textarea
                    id="availability-rules"
                    rows={6}
                    value={rulesText}
                    spellCheck={false}
                    placeholder={t("tasks.availability.dialog.rulesPlaceholder")}
                    onChange={(event) => setRulesText(event.target.value)}
                    className="font-mono text-xs"
                  />
                  {rulesError ? (
                    <p className="text-xs text-destructive">{rulesError}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">{t("tasks.availability.dialog.rulesHint")}</p>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <Label className="font-normal">{t("common.active")}</Label>
                  <p className="text-xs text-muted-foreground">{t("tasks.availability.dialog.activeHint")}</p>
                </div>
                <Switch checked={isActive} onCheckedChange={setIsActive} />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <Label className="font-normal">{t("tasks.availability.columns.default")}</Label>
                  <p className="text-xs text-muted-foreground">{t("tasks.availability.dialog.defaultHint")}</p>
                </div>
                <Switch checked={isDefault} onCheckedChange={setIsDefault} />
              </div>
            </div>
          </div>
          <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("buttons.cancel")}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isEdit ? t("buttons.save") : t("tasks.availability.dialog.addConfirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
